import React, { useState } from "react"
import { Head, Link, router } from "@inertiajs/react"
import { ExternalLink, UserRound, Music2, Disc, ChevronRight, Edit, Trash2, Plus, Camera } from "lucide-react"
import { PublicShell } from "@/components/PublicShell"
import { ResearchAssistantPanel } from "@/components/ResearchAssistantPanel"
import { ImageGallery, Lightbox, type GalleryImage } from "@/components/ImageGallery"
import { PhotosTab } from "@/components/PhotosTab"
import { RequestDeletionButton } from "@/components/RequestDeletionButton"
import { videoEmbedUrl } from "@/lib/videoEmbed"
import { formatGigDate, type GigDatePrecision } from "@/lib/gigDates"

type Image = { url: string; og_image_url: string; alt_text: string }

type BandMembership = {
  id: number
  member_id: number
  name: string
  slug: string
  instruments: string[]
  joined_year: number | null
  left_year: number | null
  importance: number
  details: string | null
  primary_image: Image | null
}

type Recording = {
  id: number
  title: string
  slug: string
  recording_type: string | null
  released_year: number | null
  highest_position: number | null
  weeks_in_chart: number | null
  record_label: string | null
  primary_image: Image | null
}

type GigAct = {
  id: number
  name: string
  slug: string | null
  type: string
  notes: string | null
  source: string | null
  is_subject: boolean
}

type GigShow = {
  date: string
  date_precision: GigDatePrecision
  end_date: string | null
  venue: { name: string; city: string; state: string; slug: string }
  acts: GigAct[]
  images: GalleryImage[]
}

type GigYear = {
  year: number | null
  shows: GigShow[]
}

type Video = { id: number; name: string; url: string; source: string }
type PersonLink = { id: number; link_text: string; url: string }
type Credit = {
  id: number
  role: string
  details: string | null
  creditable_type: string
  creditable_slug: string | null
  creditable_title: string | null
  creditable_image: Image | null
}

type PersonDetail = {
  id: number
  name: string
  slug: string
  content: string | null
  in_amh: boolean
  all_recordings_added: boolean
  birth_date: string | null
  birth_date_precision: string | null
  birth_place: string | null
  death_date: string | null
  death_date_precision: string | null
  death_place: string | null
  death_age: number | null
  members_count: number
  recordings_count: number
  gigs_count: number
  credits_count: number
  primary_image: Image | null
  images: GalleryImage[]
  bands: BandMembership[]
  recordings: Recording[]
  credits: Credit[]
  gigs: GigYear[]
  videos: Video[]
  links: PersonLink[]
}

type Permissions = { can_edit: boolean; can_delete: boolean; can_request_deletion: boolean }
type Tab = "bands" | "recordings" | "credits" | "gigs" | "videos" | "links" | "photos"

const ALL_TABS: { id: Tab; label: string }[] = [
  { id: "bands",      label: "Bands" },
  { id: "recordings", label: "Recordings" },
  { id: "credits",    label: "Credits" },
  { id: "gigs",       label: "Gigs" },
  { id: "videos",     label: "Videos" },
  { id: "links",      label: "Links" },
  { id: "photos",     label: "Photos" },
]

const AU_STATES = [ "ACT", "NSW", "NT", "QLD", "SA", "TAS", "VIC", "WA" ]

const MONTHS = [ "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec" ]

function formatDate(dateStr: string | null, precision: string | null): string | null {
  if (!dateStr || !precision || precision === "unknown") return null
  const [ year, month, day ] = dateStr.split("-").map(Number)
  const monthName = MONTHS[month - 1]
  if (precision === "day") return `${day} ${monthName} ${year}`
  if (precision === "day_month") return `${day} ${monthName}`
  if (precision === "month") return `${monthName} ${year}`
  if (precision === "year") return `${year}`
  return null
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="py-14 text-center">
      <Music2 className="w-5 h-5 text-stone-300 dark:text-stone-700 mx-auto mb-2" />
      <p className="text-sm text-stone-400 dark:text-stone-500">{message}</p>
    </div>
  )
}

function GigsTab({ gigs, canEdit, returnTo }: { gigs: GigYear[]; canEdit: boolean; returnTo: string }) {
  const [ expandedYears, setExpandedYears ] = useState<Set<number | null>>(
    () => new Set(gigs.slice(-3).map((g) => g.year))
  )
  const [ stateFilter, setStateFilter ] = useState("")
  const [ gigLightbox, setGigLightbox ] = useState<{ images: GalleryImage[]; startIndex: number } | null>(null)

  const allStates = AU_STATES.filter((s) =>
    gigs.some((yr) => yr.shows.some((sh) => sh.venue.state === s))
  )
  const filtered = stateFilter
    ? gigs.map((yr) => ({ ...yr, shows: yr.shows.filter((sh) => sh.venue.state === stateFilter) })).filter((yr) => yr.shows.length > 0)
    : gigs
  const totalShows = filtered.reduce((n, yr) => n + yr.shows.length, 0)

  if (gigs.length === 0) return <EmptyState message="No gigs on record yet." />

  function toggle(year: number | null) {
    setExpandedYears((prev) => {
      const next = new Set(prev)
      next.has(year) ? next.delete(year) : next.add(year)
      return next
    })
  }

  return (
    <div>
      <div className="flex items-center gap-3 mb-4 pb-4 border-b border-stone-100 dark:border-stone-800">
        <select
          value={stateFilter}
          onChange={(e) => {
            setStateFilter(e.target.value)
            setExpandedYears(new Set(filtered.slice(0, 3).map((g) => g.year)))
          }}
          className="bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-700 rounded-lg text-xs text-stone-700 dark:text-stone-300 px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-amber-500 transition-colors"
        >
          <option value="">All states</option>
          {allStates.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <span className="text-xs text-stone-500 dark:text-stone-400 tabular-nums" style={{ fontFamily: "'IBM Plex Mono', ui-monospace, monospace" }}>
          {totalShows} show{totalShows !== 1 ? "s" : ""} · {filtered.length} year{filtered.length !== 1 ? "s" : ""}
        </span>
      </div>

      <div className="divide-y divide-stone-100 dark:divide-stone-800/60">
        {filtered.map(({ year, shows }) => {
          const open = expandedYears.has(year)
          return (
            <div key={year ?? "unknown"}>
              <button
                onClick={() => toggle(year)}
                className="w-full flex items-center gap-2 py-3 text-left hover:bg-stone-50 dark:hover:bg-stone-800/40 -mx-1 px-1 rounded transition-colors"
              >
                <ChevronRight className={`w-3.5 h-3.5 text-stone-400 shrink-0 transition-transform duration-150 ${open ? "rotate-90" : ""}`} />
                <span className="text-sm font-semibold text-stone-900 dark:text-white tabular-nums" style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif" }}>
                  {year ?? "Year unknown"}
                </span>
                <span className="text-xs text-stone-500 dark:text-stone-400 tabular-nums" style={{ fontFamily: "'IBM Plex Mono', ui-monospace, monospace" }}>
                  · {shows.length} show{shows.length !== 1 ? "s" : ""}
                </span>
              </button>

              {open && (
                <div className="mb-2 divide-y divide-stone-50 dark:divide-stone-800/40 ml-5">
                  {shows.map((show) => (
                    <div key={show.date} className="py-2.5 flex items-start gap-3">
                      <span className="text-xs text-stone-500 dark:text-stone-400 shrink-0 w-20 tabular-nums mt-0.5" style={{ fontFamily: "'IBM Plex Mono', ui-monospace, monospace" }}>
                        {formatGigDate(show.date, show.end_date, show.date_precision)}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="flex gap-3">
                          {show.images.length > 0 && (
                            <button
                              type="button"
                              onClick={() => setGigLightbox({ images: show.images, startIndex: 0 })}
                              className="relative shrink-0 w-20 h-16 rounded overflow-hidden bg-stone-100 dark:bg-stone-800 hover:opacity-80 transition-opacity"
                              aria-label="View gig photo"
                            >
                              <img src={show.images[0].thumb_url ?? undefined} alt="Gig photo" className="w-full h-full object-cover" />
                              {show.images.length > 1 && (
                                <span className="absolute bottom-0.5 right-0.5 text-[9px] font-medium bg-black/60 text-white rounded px-1 leading-4">
                                  {show.images.length}
                                </span>
                              )}
                            </button>
                          )}
                          <div className="flex-1 min-w-0">
                            <Link
                              href={`/venues/${show.venue.slug}`}
                              className="text-sm font-medium text-stone-900 dark:text-white hover:text-amber-600 dark:hover:text-amber-400 transition-colors leading-snug no-underline"
                              style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif" }}
                            >
                              {show.venue.name}
                            </Link>
                            <p className="text-xs text-stone-500 dark:text-stone-400" style={{ fontFamily: "'IBM Plex Mono', ui-monospace, monospace" }}>
                              {show.venue.city}, {show.venue.state}
                            </p>
                            {show.acts.length > 1 && (
                              <p className="text-xs text-stone-500 dark:text-stone-400 mt-0.5">
                                <span className="text-stone-400 dark:text-stone-500">Also appearing: </span>
                                {show.acts.filter((a) => !a.is_subject).map((a) => (
                                  a.slug
                                    ? <Link key={a.name} href={`/${a.type === "Band" ? "bands" : "people"}/${a.slug}`} className="hover:text-amber-600 dark:hover:text-amber-400 no-underline">{a.name}</Link>
                                    : <span key={a.name}>{a.name}</span>
                                )).reduce<React.ReactNode[]>((acc, el, i) => i === 0 ? [el] : [...acc, ", ", el], [])}
                              </p>
                            )}
                            {show.acts.find((a) => a.is_subject)?.notes && (
                              <p className="text-xs text-stone-400 dark:text-stone-500 mt-0.5 italic">
                                {show.acts.find((a) => a.is_subject)!.notes}
                              </p>
                            )}
                            {show.acts.find((a) => a.is_subject)?.source && (
                              <p className="text-xs text-stone-400 dark:text-stone-500 mt-0.5">
                                Source: {show.acts.find((a) => a.is_subject)!.source}
                              </p>
                            )}
                            {canEdit && (() => {
                              const subjectAct = show.acts.find((a) => a.is_subject)
                              return subjectAct ? (
                                <div className="flex items-center gap-3 mt-1">
                                  <Link
                                    href={`/gigs/${subjectAct.id}/edit?return_to=${encodeURIComponent(returnTo)}`}
                                    className="inline-flex items-center gap-1 text-xs text-stone-400 hover:text-amber-600 dark:hover:text-amber-400 no-underline transition-colors"
                                  >
                                    <Edit className="w-3 h-3" /> Edit
                                  </Link>
                                  <Link
                                    href={`/admin/image_attachments/new?attachable_type=Gig&attachable_id=${subjectAct.id}&return_to=${encodeURIComponent(returnTo)}`}
                                    className="inline-flex items-center gap-1 text-xs text-stone-400 hover:text-amber-600 dark:hover:text-amber-400 no-underline transition-colors"
                                  >
                                    <Camera className="w-3 h-3" /> Photo
                                  </Link>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      if (confirm("Delete this gig?")) {
                                        router.delete(`/gigs/${subjectAct.id}`, { data: { return_to: returnTo } })
                                      }
                                    }}
                                    className="inline-flex items-center gap-1 text-xs text-stone-400 hover:text-red-500 dark:hover:text-red-400 transition-colors"
                                  >
                                    <Trash2 className="w-3 h-3" /> Delete
                                  </button>
                                </div>
                              ) : null
                            })()}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )
        })}
      </div>

      {gigLightbox && (
        <Lightbox images={gigLightbox.images} startIndex={gigLightbox.startIndex} onClose={() => setGigLightbox(null)} />
      )}
    </div>
  )
}

export default function PersonShow({ person, permissions }: { person: PersonDetail; permissions: Permissions }) {

  const tabCounts: Record<Tab, number> = {
    bands:      person.bands.length,
    recordings: person.recordings.length,
    credits:    person.credits.length,
    gigs:       person.gigs_count,
    videos:     person.videos.length,
    links:      person.links.length,
    photos:     person.images.length,
  }
  const visibleTabs = ALL_TABS.filter((t) => tabCounts[t.id] > 0 || permissions.can_edit)
  const [ activeTab, setActiveTab ] = useState<Tab>(() => visibleTabs[0]?.id ?? "bands")

  const birthDate = formatDate(person.birth_date, person.birth_date_precision)
  const deathDate = formatDate(person.death_date, person.death_date_precision)

  function handleDelete() {
    if (confirm(`Delete "${person.name}"? This cannot be undone.`)) {
      router.delete(`/people/${person.slug}`)
    }
  }

  const description = person.content?.replace(/<[^>]+>/g, "").slice(0, 160) ?? `${person.name} — Australian Music Database.`

  return (
    <>
      <Head title={person.name}>
        <meta name="description" content={description} />
        <meta property="og:title" content={person.name} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="profile" />
        {person.primary_image?.og_image_url && <meta property="og:image" content={person.primary_image.og_image_url} />}
        <meta name="twitter:card" content="summary_large_image" />
      </Head>

      <PublicShell>
        {/* Hero + header */}
        <div className="flex flex-col sm:flex-row sm:items-start gap-6 mb-8">
          <ImageGallery
            images={person.images ?? []}
            emptyIcon={<UserRound className="w-14 h-14 text-stone-300 dark:text-stone-600" strokeWidth={0.75} />}
          />

          <div className="flex-1 min-w-0">
            <div className="flex items-start gap-2 flex-wrap mb-2">
              <h1
                className="text-2xl sm:text-3xl font-bold text-stone-900 dark:text-white leading-tight tracking-tight"
                style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif" }}
              >
                {person.name}
              </h1>
              {person.in_amh && (
                <span className="mt-1 sm:mt-1.5 inline-flex items-center text-xs font-medium text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 px-2 py-0.5 rounded-full shrink-0 leading-none">
                  AMH feature
                </span>
              )}
            </div>

            <div className="flex items-center gap-1 text-xs text-stone-500 dark:text-stone-400 mb-3" style={{ fontFamily: "'IBM Plex Mono', ui-monospace, monospace" }}>
              <span>{person.members_count} band{person.members_count !== 1 ? "s" : ""}</span>
              <span className="mx-1 text-stone-300 dark:text-stone-700">·</span>
              <span>{person.recordings_count} recordings</span>
              <span className="mx-1 text-stone-300 dark:text-stone-700">·</span>
              <span>{person.credits_count} credits</span>
              <span className="mx-1 text-stone-300 dark:text-stone-700">·</span>
              <span>{person.gigs_count} gigs</span>
            </div>

            {(birthDate || person.birth_place || deathDate || person.death_place) && (
              <div className="mb-3 text-xs text-stone-500 dark:text-stone-400 space-y-0.5" style={{ fontFamily: "'IBM Plex Mono', ui-monospace, monospace" }}>
                {(birthDate || person.birth_place) && (
                  <p>
                    <span className="text-stone-400 dark:text-stone-500">b.</span>{" "}
                    {[ birthDate, person.birth_place ].filter(Boolean).join(", ")}
                  </p>
                )}
                {(deathDate || person.death_place) && (
                  <p>
                    <span className="text-stone-400 dark:text-stone-500">d.</span>{" "}
                    {[ deathDate, person.death_place ].filter(Boolean).join(", ")}
                    {person.death_age !== null && <span className="text-stone-400 dark:text-stone-500"> (aged {person.death_age})</span>}
                  </p>
                )}
              </div>
            )}

            {person.content && (
              <div
                className="text-sm text-stone-600 dark:text-stone-400 leading-relaxed mb-4 [&_p]:mb-2 [&_p:last-child]:mb-0"
                dangerouslySetInnerHTML={{ __html: person.content }}
              />
            )}

            {permissions.can_edit && (
              <div className="flex items-center gap-2 flex-wrap">
                <Link
                  href={`/people/${person.slug}/edit`}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-stone-700 dark:text-stone-300 bg-stone-100 dark:bg-stone-800 hover:bg-stone-200 dark:hover:bg-stone-700 rounded-lg transition-colors no-underline"
                >
                  <Edit className="w-3.5 h-3.5" />
                  Edit
                </Link>
                {permissions.can_delete && (
                  <button
                    onClick={handleDelete}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/40 hover:bg-red-100 dark:hover:bg-red-950/60 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    Delete
                  </button>
                )}
                {permissions.can_request_deletion && (
                  <RequestDeletionButton type="Person" id={person.id} name={person.name} />
                )}
              </div>
            )}
          </div>
        </div>

        {/* Tab bar */}
        <div className="border-b border-stone-200 dark:border-stone-800 mb-6 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8">
          <div className="flex gap-0 -mb-px overflow-x-auto">
            {visibleTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={[
                  "px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors focus-visible:outline-none",
                  activeTab === tab.id
                    ? "border-amber-500 text-amber-600 dark:text-amber-400"
                    : "border-transparent text-stone-500 dark:text-stone-400 hover:text-stone-900 dark:hover:text-white hover:border-stone-300 dark:hover:border-stone-600",
                ].join(" ")}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Bands */}
        {activeTab === "bands" && (
          <div>
            {permissions.can_edit && (
              <div className="mb-4 flex justify-end">
                <Link href={`/members/new?person_id=${person.id}&return_to=/people/${person.slug}`} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-stone-700 dark:text-stone-300 bg-stone-100 dark:bg-stone-800 hover:bg-stone-200 dark:hover:bg-stone-700 rounded-lg transition-colors no-underline">
                  <Plus className="w-3.5 h-3.5" />
                  Add band
                </Link>
              </div>
            )}
            {person.bands.length === 0 ? (
              <EmptyState message="No band memberships on record yet." />
            ) : (
              <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))" }}>
                {person.bands.map((band) => (
                  <div
                    key={band.id}
                    className="group bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-800 rounded-xl overflow-hidden hover:border-amber-300 dark:hover:border-amber-700 hover:shadow-sm transition-all duration-150"
                  >
                    <Link href={`/bands/${band.slug}`} className="block no-underline">
                      <div className="aspect-square bg-stone-100 dark:bg-stone-800 overflow-hidden">
                        {band.primary_image ? (
                          <img src={band.primary_image.url} alt={band.primary_image.alt_text} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Music2 className="w-16 h-16 text-stone-300 dark:text-stone-600" strokeWidth={0.75} />
                          </div>
                        )}
                      </div>
                      <div className="p-3">
                        <div className="text-sm font-semibold text-stone-900 dark:text-white group-hover:text-amber-600 dark:group-hover:text-amber-400 transition-colors leading-snug truncate" style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif" }}>
                          {band.name}
                        </div>
                        {band.instruments.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1.5">
                            {band.instruments.map((inst) => (
                              <span key={inst} className="inline-flex items-center text-xs px-1.5 py-0.5 rounded-full bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-800 leading-none">
                                {inst}
                              </span>
                            ))}
                          </div>
                        )}
                        {(band.joined_year || band.left_year) && (
                          <div className="text-xs text-stone-500 dark:text-stone-400 mt-1.5 tabular-nums" style={{ fontFamily: "'IBM Plex Mono', ui-monospace, monospace" }}>
                            {band.joined_year ?? "?"}{band.left_year ? `–${band.left_year}` : ""}
                          </div>
                        )}
                        {band.details && (
                          <div className="text-xs text-stone-500 dark:text-stone-400 mt-1 leading-snug" style={{ fontFamily: "'IBM Plex Mono', ui-monospace, monospace" }} title={band.details}>
                            {band.details.length > 80 ? band.details.slice(0, 80) + "…" : band.details}
                          </div>
                        )}
                      </div>
                    </Link>
                    {permissions.can_edit && (
                      <div className="flex gap-3 px-3 pb-2.5 pt-0 border-t border-stone-100 dark:border-stone-800">
                        <Link
                          href={`/members/${band.member_id}/edit?return_to=/people/${person.slug}`}
                          className="text-xs text-stone-400 hover:text-amber-600 dark:hover:text-amber-400 transition-colors no-underline pt-2"
                        >
                          Edit
                        </Link>
                        <button
                          onClick={() => {
                            if (confirm(`Remove ${person.name} from ${band.name}?`)) {
                              router.delete(`/members/${band.member_id}`, { data: { return_to: `/people/${person.slug}` } })
                            }
                          }}
                          className="text-xs text-red-400 hover:text-red-600 transition-colors pt-2"
                        >
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Recordings */}
        {activeTab === "recordings" && (
          <div>
            {permissions.can_edit && (
              <div className="mb-4 flex justify-end">
                <Link href={`/recordings/new?recordable_type=Person&recordable_id=${person.id}&return_to=/people/${person.slug}`} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-stone-700 dark:text-stone-300 bg-stone-100 dark:bg-stone-800 hover:bg-stone-200 dark:hover:bg-stone-700 rounded-lg transition-colors no-underline">
                  <Plus className="w-3.5 h-3.5" />
                  Add recording
                </Link>
              </div>
            )}
            {person.recordings.length === 0 ? (
              <EmptyState message="No recordings on record yet." />
            ) : (
              <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))" }}>
                {person.recordings.map((rec) => (
                  <div
                    key={rec.id}
                    className="group bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-800 rounded-xl overflow-hidden hover:border-amber-300 dark:hover:border-amber-700 hover:shadow-sm transition-all duration-150"
                  >
                    <Link href={`/recordings/${rec.slug}`} className="block no-underline">
                      <div className="aspect-square bg-stone-100 dark:bg-stone-800 overflow-hidden">
                        {rec.primary_image ? (
                          <img src={rec.primary_image.url} alt={rec.primary_image.alt_text} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Disc className="w-16 h-16 text-stone-300 dark:text-stone-600" strokeWidth={0.75} />
                          </div>
                        )}
                      </div>
                      <div className="p-3">
                        <div className="text-sm font-semibold text-stone-900 dark:text-white group-hover:text-amber-600 dark:group-hover:text-amber-400 transition-colors leading-snug truncate" style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif" }}>
                          {rec.title}
                        </div>
                        <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                          {rec.recording_type && (
                            <span className="text-xs px-1.5 py-px rounded bg-stone-100 dark:bg-stone-800 text-stone-500 dark:text-stone-400 leading-none border border-stone-200 dark:border-stone-700">
                              {rec.recording_type}
                            </span>
                          )}
                          {rec.released_year && (
                            <span className="text-xs text-stone-500 dark:text-stone-400 tabular-nums" style={{ fontFamily: "'IBM Plex Mono', ui-monospace, monospace" }}>
                              {rec.released_year}
                            </span>
                          )}
                        </div>
                        {rec.highest_position !== null && (
                          <div className="flex items-center gap-1.5 mt-1.5">
                            <span
                              className={`text-xs font-bold tabular-nums ${rec.highest_position === 1 ? "text-amber-500" : "text-stone-500 dark:text-stone-400"}`}
                              style={{ fontFamily: "'IBM Plex Mono', ui-monospace, monospace" }}
                            >
                              #{rec.highest_position}
                            </span>
                            {rec.weeks_in_chart && (
                              <span className="text-xs text-stone-500 dark:text-stone-400 tabular-nums" style={{ fontFamily: "'IBM Plex Mono', ui-monospace, monospace" }}>
                                · {rec.weeks_in_chart}wks
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </Link>
                    {permissions.can_edit && (
                      <div className="flex gap-3 px-3 pb-2.5 pt-0 border-t border-stone-100 dark:border-stone-800">
                        <Link
                          href={`/recordings/${rec.slug}/edit?return_to=/people/${person.slug}`}
                          className="text-xs text-stone-400 hover:text-amber-600 dark:hover:text-amber-400 transition-colors no-underline pt-2"
                        >
                          Edit
                        </Link>
                        <button
                          onClick={() => {
                            if (confirm(`Delete "${rec.title}"? This cannot be undone.`)) {
                              router.delete(`/recordings/${rec.slug}`, { data: { return_to: `/people/${person.slug}` } })
                            }
                          }}
                          className="text-xs text-red-400 hover:text-red-600 transition-colors pt-2"
                        >
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Credits */}
        {activeTab === "credits" && (
          <div>
            {permissions.can_edit && (
              <div className="mb-4 flex justify-end">
                <Link href={`/credits/new?person_id=${person.id}&return_to=/people/${person.slug}`} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-stone-700 dark:text-stone-300 bg-stone-100 dark:bg-stone-800 hover:bg-stone-200 dark:hover:bg-stone-700 rounded-lg transition-colors no-underline">
                  <Plus className="w-3.5 h-3.5" />
                  Add credit
                </Link>
              </div>
            )}
            <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))" }}>
              {person.credits.map((credit) => {
                const href = credit.creditable_slug
                  ? `/${credit.creditable_type === "Recording" ? "recordings" : "bands"}/${credit.creditable_slug}`
                  : null
                const cardContent = (
                  <>
                    <div className="aspect-square overflow-hidden relative">
                      {credit.creditable_image ? (
                        <>
                          <img
                            src={credit.creditable_image.url}
                            alt={credit.creditable_image.alt_text}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent px-2 pb-1.5 pt-4">
                            <span
                              className="text-xs font-bold text-white leading-none"
                              style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif" }}
                            >
                              {credit.role}
                            </span>
                          </div>
                        </>
                      ) : (
                        <div className="w-full h-full bg-amber-50 dark:bg-amber-950/30 flex items-center justify-center border-b border-amber-100 dark:border-amber-900/40">
                          <span
                            className="text-lg font-bold text-amber-600 dark:text-amber-400 text-center px-2 leading-tight"
                            style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif" }}
                          >
                            {credit.role}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="p-3">
                      <div className="text-sm font-semibold text-stone-900 dark:text-white group-hover:text-amber-600 dark:group-hover:text-amber-400 transition-colors leading-snug truncate" style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif" }}>
                        {credit.creditable_title ?? credit.creditable_type}
                      </div>
                      <span className="text-xs text-stone-500 dark:text-stone-400" style={{ fontFamily: "'IBM Plex Mono', ui-monospace, monospace" }}>
                        {credit.creditable_type}
                      </span>
                      {credit.details && (
                        <p className="text-xs text-stone-500 dark:text-stone-400 mt-1 truncate">{credit.details}</p>
                      )}
                    </div>
                  </>
                )
                return (
                  <div
                    key={credit.id}
                    className="group bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-800 rounded-xl overflow-hidden hover:border-amber-300 dark:hover:border-amber-700 hover:shadow-sm transition-all duration-150"
                  >
                    {href ? (
                      <Link href={href} className="block no-underline">{cardContent}</Link>
                    ) : (
                      <div>{cardContent}</div>
                    )}
                    {permissions.can_edit && (
                      <div className="flex gap-3 px-3 pb-2.5 pt-0 border-t border-stone-100 dark:border-stone-800">
                        <Link
                          href={`/credits/${credit.id}/edit?return_to=/people/${person.slug}`}
                          className="text-xs text-stone-400 hover:text-amber-600 dark:hover:text-amber-400 transition-colors no-underline pt-2"
                        >
                          Edit
                        </Link>
                        <button
                          onClick={() => {
                            if (confirm(`Delete this credit?`)) {
                              router.delete(`/credits/${credit.id}`, { data: { return_to: `/people/${person.slug}` } })
                            }
                          }}
                          className="text-xs text-red-400 hover:text-red-600 transition-colors pt-2"
                        >
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {/* Gigs */}
        {activeTab === "gigs" && (
          <div>
            {permissions.can_edit && (
              <div className="mb-4 flex justify-end">
                <Link href={`/gigs/new?gigable_type=Person&gigable_id=${person.id}&return_to=/people/${person.slug}`} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-stone-700 dark:text-stone-300 bg-stone-100 dark:bg-stone-800 hover:bg-stone-200 dark:hover:bg-stone-700 rounded-lg transition-colors no-underline">
                  <Plus className="w-3.5 h-3.5" />
                  Add gig
                </Link>
              </div>
            )}
            <GigsTab gigs={person.gigs} canEdit={permissions.can_edit} returnTo={`/people/${person.slug}`} />
          </div>
        )}

        {/* Videos */}
        {activeTab === "videos" && (
          <div>
            {permissions.can_edit && (
              <div className="mb-4">
                <Link
                  href={`/videos/new?pre_attach_type=Person&pre_attach_id=${person.id}&return_to=${encodeURIComponent(`/people/${person.slug}`)}`}
                  className="inline-flex items-center gap-1.5 text-sm text-stone-500 hover:text-amber-600 dark:hover:text-amber-400 no-underline transition-colors"
                >
                  <Plus className="w-3.5 h-3.5" /> Add video
                </Link>
              </div>
            )}
            {person.videos.length === 0 ? (
              <EmptyState message="No videos on record yet." />
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {person.videos.map((video) => (
                  <div key={video.id} className="rounded-xl overflow-hidden border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900">
                    <div className="aspect-video w-full">
                      <iframe
                        src={videoEmbedUrl(video.url)}
                        title={video.name}
                        className="w-full h-full"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                    <div className="px-3.5 py-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <span className="text-sm font-medium text-stone-700 dark:text-stone-300 truncate block" style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif" }}>
                            {video.name}
                          </span>
                          {video.source !== "direct" && (
                            <span className="text-xs text-stone-500 dark:text-stone-400 truncate block" style={{ fontFamily: "'IBM Plex Mono', ui-monospace, monospace" }}>
                              from {video.source}
                            </span>
                          )}
                        </div>
                        {permissions.can_edit && video.source === "direct" && (
                          <div className="flex items-center gap-2 shrink-0">
                            <Link
                              href={`/videos/${video.id}/edit?return_to=${encodeURIComponent(`/people/${person.slug}`)}`}
                              className="inline-flex items-center gap-1 text-xs text-stone-400 hover:text-amber-600 dark:hover:text-amber-400 no-underline transition-colors"
                            >
                              <Edit className="w-3 h-3" /> Edit
                            </Link>
                            <button
                              type="button"
                              onClick={() => {
                                if (confirm("Delete this video?")) {
                                  router.delete(`/videos/${video.id}`, { data: { return_to: `/people/${person.slug}` } })
                                }
                              }}
                              className="inline-flex items-center gap-1 text-xs text-stone-400 hover:text-red-500 dark:hover:text-red-400 transition-colors"
                            >
                              <Trash2 className="w-3 h-3" /> Delete
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Links */}
        {activeTab === "links" && (
          <div>
            {person.links.length === 0 && !permissions.can_edit ? (
              <EmptyState message="No links on record yet." />
            ) : (
              <div className="divide-y divide-stone-100 dark:divide-stone-800/80">
                {person.links.map((link) => (
                  <div key={link.id} className="flex items-center gap-3.5 py-3.5 group/lnk">
                    <a
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3.5 flex-1 min-w-0 no-underline group"
                    >
                      <div className="w-8 h-8 rounded-lg bg-stone-100 dark:bg-stone-800 flex items-center justify-center shrink-0 group-hover:bg-amber-50 dark:group-hover:bg-amber-950/40 border border-stone-200 dark:border-stone-700 group-hover:border-amber-200 dark:group-hover:border-amber-800 transition-colors">
                        <ExternalLink className="w-3.5 h-3.5 text-stone-400 group-hover:text-amber-500 transition-colors" />
                      </div>
                      <span className="text-sm font-medium text-stone-900 dark:text-white group-hover:text-amber-600 dark:group-hover:text-amber-400 transition-colors leading-snug flex-1">
                        {link.link_text}
                      </span>
                      <ChevronRight className="w-4 h-4 text-stone-300 dark:text-stone-600 shrink-0" />
                    </a>
                    {permissions.can_edit && (
                      <div className="flex items-center gap-1 opacity-0 group-hover/lnk:opacity-100 transition-opacity shrink-0">
                        <Link href={`/links/${link.id}/edit?return_to=/people/${person.slug}`} className="p-1 rounded text-stone-400 hover:text-amber-600 no-underline" title="Edit"><Edit className="w-3.5 h-3.5" /></Link>
                        <button type="button" onClick={() => { if (confirm("Delete this link?")) router.delete(`/links/${link.id}`, { data: { return_to: `/people/${person.slug}` } }) }} className="p-1 rounded text-stone-400 hover:text-red-600" title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
            {permissions.can_edit && (
              <div className="mt-4">
                <Link href={`/links/new?linkable_type=Person&linkable_id=${person.id}&return_to=/people/${person.slug}`} className="inline-flex items-center gap-1 text-sm text-stone-500 hover:text-amber-600 no-underline">
                  <Plus className="w-3.5 h-3.5" />Add link
                </Link>
              </div>
            )}
          </div>
        )}

        {activeTab === "photos" && (
          <PhotosTab
            images={person.images}
            adminControls={permissions.can_edit ? {
              setPrimaryBasePath: "/admin/image_attachments",
              removeBasePath:     "/admin/image_attachments",
              deleteBasePath:     "/admin/image_attachments",
              addImageHref:       `/admin/images/new?attachable_type=Person&attachable_id=${person.id}&return_to=/people/${person.slug}`,
              attachExistingHref: `/admin/image_attachments/new?attachable_type=Person&attachable_id=${person.id}&return_to=/people/${person.slug}`,
              returnTo:           `/people/${person.slug}`,
            } : undefined}
          />
        )}

        {permissions.can_edit && (
          <ResearchAssistantPanel entityType="Person" entityId={person.id} />
        )}
      </PublicShell>
    </>
  )
}
